import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3, CalendarDays, CheckCircle2, CircleDot, Clock, Mail, MessageSquare, PauseCircle, PenLine, Send, Users,
} from 'lucide-react';
import {
  ANTWORT_INFO, STAND_INFO, VERSAND_INFO, brevoStatusText, datumKurz, datumZeit, empfaengerText, prozent, tageText, zeitraumText,
  antwortenText, type AnfrageAntwort, type NaechsterSchritt, type PlanungStand, type PlanungsMail,
} from '../../lib/planung';
import { AvatarStapel } from './Avatar';
import { SCHRITT_INFO } from './schrittInfo';
import './PlanungsChips.css';

/** Bearbeitungsstand: In Arbeit · Wartet auf Feedback · Feedback einarbeiten · Abgestimmt */
export function StandBadge({ stand }: { stand: PlanungStand }) {
  const s = STAND_INFO[stand];
  return <span className={`badge ${s.badge}`} title={s.text}><span className="badge-dot" />{s.label}</span>;
}

/** Wann geht die Mail raus — und woher weiß das Mailtool das? */
export function VersandChip({ mail }: { mail: PlanungsMail }) {
  const { am, art } = mail.versand;
  if (!am || !art) {
    return <span className="pl-chip pl-chip--leer"><Send size={13} aria-hidden="true" />Kein Versandtermin</span>;
  }
  const info = VERSAND_INFO[art];
  const Icon = art === 'versendet' ? CheckCircle2 : art === 'angehalten' ? PauseCircle : Send;
  const relativ = art === 'versendet' ? '' : tageText(am);
  // Angehalten mit eigenem Termin unter „Termine & Stand“: der gilt, Brevo hat noch den alten.
  const brevoAm = art === 'angehalten' ? mail.versand.brevo_am ?? null : null;
  const titel = brevoAm
    ? `In Brevo noch für ${datumZeit(brevoAm)} eingetragen — dort anpassen und freigeben`
    : `${info.label}: ${datumZeit(am)}`;
  return (
    <span className={`pl-chip pl-chip--versand-${art}`} title={titel}>
      <Icon size={13} aria-hidden="true" />
      <span className="pl-chip-text">{art === 'versendet' ? 'Versendet ' : ''}{datumZeit(am)}</span>
      {relativ && <span className="pl-chip-zusatz">{relativ}</span>}
      {art !== 'versendet' && art !== 'eingeplant' && (
        <span className="pl-chip-zusatz">
          {art === 'angehalten' ? (brevoAm ? 'in Brevo angehalten' : 'angehalten') : art === 'geplant' ? 'geplant' : art === 'wunsch' ? 'Wunsch' : 'laut Name'}
        </span>
      )}
    </span>
  );
}

/** Wann findet das Event statt, um das es geht? */
export function EventChip({ mail }: { mail: PlanungsMail }) {
  const { beginn, ende, titel } = mail.event;
  if (!beginn) return null;
  return (
    <span className="pl-chip pl-chip--event" title={`Event${titel ? ` „${titel}"` : ''}: ${zeitraumText(beginn, ende)}`}>
      <CalendarDays size={13} aria-hidden="true" />
      <span className="pl-chip-text">{zeitraumText(beginn, ende)}</span>
    </span>
  );
}

/** Wie viele Postfächer bekommen die Mail? Nur Zahlen und Gruppennamen, nie Adressen. */
export function EmpfaengerChip({ mail }: { mail: PlanungsMail }) {
  const e = mail.empfaenger;
  const gruppen = e.gruppen.map((g) => `${g.name} (${g.anzahl.toLocaleString('de-DE')})`).join(', ');
  const titel = [
    gruppen ? `Zielgruppen: ${gruppen}` : 'Noch keine Zielgruppe',
    e.manuell ? `${e.manuell} einzeln eingetragen` : '',
    e.mehrere ? 'Wer in mehreren Gruppen steht, bekommt die Mail nur einmal.' : '',
    e.intern.length ? `Interne Kopie: ${e.intern.join(', ')}` : '',
  ].filter(Boolean).join('\n');
  return (
    <span className={`pl-chip${e.summe + e.manuell === 0 ? ' pl-chip--warn' : ''}`} title={titel}>
      <Users size={13} aria-hidden="true" />
      <span className="pl-chip-text">{empfaengerText(e)}</span>
    </span>
  );
}

/** Status in Brevo, wenn es dort eine Kampagne gibt. */
export function BrevoChip({ mail }: { mail: PlanungsMail }) {
  if (!mail.brevo_id) return null;
  // Versendete ohne gelesenen Status: Brevo hat sie verschickt.
  const s = mail.brevo_status ?? (mail.status === 'sent' ? 'sent' : null);
  const ton = s === 'suspended' ? ' pl-chip--warn' : s === 'queued' || s === 'in_process' ? ' pl-chip--info' : '';
  return (
    <span className={`pl-chip${ton}`} title={`${s ? brevoStatusText(s) : `Brevo-Kampagne #${mail.brevo_id}, Status noch nicht gelesen`}${mail.brevo_veraltet ? ' — im Mailtool gibt es neuere Änderungen, die noch nicht in Brevo sind' : ''}`}>
      <Mail size={13} aria-hidden="true" />
      <span className="pl-chip-text">{s ? brevoStatusText(s) : 'Brevo-Status wird gelesen'}</span>
      {mail.brevo_veraltet && <span className="pl-chip-zusatz pl-chip-zusatz--warn">nicht aktuell</span>}
    </span>
  );
}

/** Sportschulen als kleine Etiketten; ohne Bezug „Alle Sportschulen". */
export function SportschulChips({ mail }: { mail: PlanungsMail }) {
  const liste = mail.sportschulen;
  if (liste.length === 0 || mail.uebergreifend) {
    return (
      <span className="pl-schulen">
        {liste.map((s) => <span key={s} className="pl-schule">{s}</span>)}
        <span className="pl-schule pl-schule--alle">{liste.length ? 'und übergreifend' : 'Alle Sportschulen'}</span>
      </span>
    );
  }
  return <span className="pl-schulen">{liste.map((s) => <span key={s} className="pl-schule">{s}</span>)}</span>;
}

/** Zahlen einer versendeten Mail. */
export function StatistikZeile({ mail }: { mail: PlanungsMail }) {
  const s = mail.statistik;
  if (!s) {
    return mail.status === 'sent' ? <span className="pl-statistik pl-statistik--leer">Statistik wird geholt …</span> : null;
  }
  return (
    <span className="pl-statistik" title={`Stand ${datumZeit(s.stand)}`}>
      <span><strong>{s.gesendet.toLocaleString('de-DE')}</strong> versendet</span>
      <span><strong>{prozent(s.zugestellt, s.gesendet)}</strong> zugestellt</span>
      <span><strong>{prozent(s.geoeffnet, s.zugestellt || s.gesendet)}</strong> geöffnet</span>
      <span><strong>{prozent(s.geklickt, s.zugestellt || s.gesendet)}</strong> geklickt</span>
      {s.abgemeldet > 0 && <span><strong>{s.abgemeldet}</strong> abgemeldet</span>}
    </span>
  );
}

/** Kommentare, offene Punkte, neue Rückmeldungen auf einen Blick. */
export function FeedbackZaehler({ mail, extern = false }: { mail: PlanungsMail; extern?: boolean }) {
  const z = mail.zaehler;
  if (z.kommentare === 0 && z.vorschlaege_offen === 0 && z.passt === 0 && (extern || z.ungelesen === 0)) return null;
  return (
    <span className="pl-zaehler">
      {z.kommentare > 0 && (
        <span className="pl-zaehler-teil" title={`${z.kommentare} Kommentare, davon ${z.offen} offen`}>
          <MessageSquare size={13} aria-hidden="true" />{z.kommentare}
          <span className="sr-only"> {z.kommentare === 1 ? 'Kommentar' : 'Kommentare'}, davon {z.offen} offen</span>
        </span>
      )}
      {z.vorschlaege_offen > 0 && (
        <span className="pl-zaehler-teil pl-zaehler-teil--vorschlag" title={`${z.vorschlaege_offen} offene Änderungsvorschläge`}>
          <PenLine size={13} aria-hidden="true" />{z.vorschlaege_offen}
          <span className="sr-only"> {z.vorschlaege_offen === 1 ? 'offener Änderungsvorschlag' : 'offene Änderungsvorschläge'}</span>
        </span>
      )}
      {z.passt > 0 && (
        <span className="pl-zaehler-teil pl-zaehler-teil--passt" title={`${z.passt}× „Passt so“`}>
          <CheckCircle2 size={13} aria-hidden="true" />{z.passt}
          <span className="sr-only">× „Passt so“</span>
        </span>
      )}
      {!extern && z.ungelesen > 0 && <span className="pl-neu" title={`${z.ungelesen} neue Rückmeldungen, noch ungelesen`}>{z.ungelesen} neu<span className="sr-only">e {z.ungelesen === 1 ? 'Rückmeldung' : 'Rückmeldungen'}</span></span>}
    </span>
  );
}

/**
 * Eine Zeile „was als Nächstes": Etikett mit Symbol (nie nur Farbe), ein Satz
 * und Kleingedrucktes. statistikLink = Pfad zur Statistik (bei versendeten).
 */
export function SchrittZeile({ schritt, id, statistikLink }: { schritt: NaechsterSchritt; id?: string; statistikLink?: string | null }) {
  const info = SCHRITT_INFO[schritt.art];
  const Icon = schritt.art === 'handeln' && schritt.ton === 'neutral' ? CircleDot : info.Icon;
  return (
    <p className={`pl-schritt pl-schritt--${schritt.ton}`} id={id}>
      <span className="pl-schritt-art">
        <Icon size={14} aria-hidden="true" />
        <span>{info.label}<span className="sr-only">:</span></span>
      </span>
      <span className="pl-schritt-text">
        <span className="pl-schritt-satz">{schritt.text}</span>
        {schritt.zusatz && <span className="pl-schritt-zusatz">{schritt.zusatz}</span>}
      </span>
      {statistikLink && (
        <Link to={statistikLink} className="pl-schritt-link">
          <BarChart3 size={13} aria-hidden="true" />Statistik<span className="sr-only"> ansehen</span>
        </Link>
      )}
    </p>
  );
}

const ANTWORT_ICON: Record<AnfrageAntwort, typeof Clock> = { passt: CheckCircle2, kommentiert: MessageSquare, offen: Clock };
const ANTWORT_KURZ: Record<AnfrageAntwort, string> = { passt: 'Passt so', kommentiert: 'hat kommentiert', offen: 'noch offen' };

/**
 * Wer ist um Feedback gebeten — und wer hat schon geantwortet? Als Knopf:
 * Zeigen (Maus), Fokus (Tastatur) oder Tippen (Handy) öffnet die Liste mit
 * Namen und Antwort; die Ansage nennt dasselbe.
 */
export function AnfragenStapel({ mail }: { mail: PlanungsMail }) {
  const [offen, setOffen] = useState(false);
  const box = useRef<HTMLSpanElement>(null);
  const id = useId();
  useEffect(() => {
    if (!offen) return;
    const weg = (e: PointerEvent) => { if (!box.current?.contains(e.target as Node)) setOffen(false); };
    const taste = (e: KeyboardEvent) => { if (e.key === 'Escape') setOffen(false); };
    document.addEventListener('pointerdown', weg);
    document.addEventListener('keydown', taste);
    return () => {
      document.removeEventListener('pointerdown', weg);
      document.removeEventListener('keydown', taste);
    };
  }, [offen]);

  const anfragen = mail.anfragen;
  if (anfragen.length === 0) return null;
  const ausstehend = anfragen.filter((a) => a.antwort === 'offen').length;
  const kurz = antwortenText(anfragen);
  const einzeln = anfragen.map((a) => `${a.name}: ${ANTWORT_KURZ[a.antwort]}`).join('; ');
  const frist = mail.feedback_bis && mail.status !== 'sent' ? `Rückmeldung erbeten bis ${datumKurz(mail.feedback_bis)}` : '';

  return (
    <span className={`pl-anfragen${offen ? ' pl-anfragen--offen' : ''}`} ref={box}>
      <button
        type="button"
        className="pl-anfragen-knopf"
        aria-expanded={offen}
        aria-controls={id}
        aria-label={`${kurz}. ${einzeln}.${frist ? ` ${frist}.` : ''}`}
        onClick={() => setOffen((o) => !o)}
      >
        <AvatarStapel namen={anfragen.map((a) => a.name)} antworten={anfragen.map((a) => a.antwort)} beschriftung={false} />
        <span className={`pl-anfragen-text${ausstehend > 0 ? '' : ' pl-anfragen-text--fertig'}`} aria-hidden="true">
          {ausstehend > 0 ? <Clock size={12} aria-hidden="true" /> : <CheckCircle2 size={12} aria-hidden="true" />}
          {kurz}
        </span>
      </button>
      <span className="pl-anfragen-pop" id={id} role="tooltip" aria-hidden="true">
        <span className="pl-anfragen-pop-titel">Um Feedback gebeten</span>
        <span className="pl-anfragen-pop-liste">
          {anfragen.map((a, i) => {
            const Icon = ANTWORT_ICON[a.antwort];
            return (
              <span key={`${a.name}-${i}`} className={`pl-anfragen-pop-zeile pl-anfragen-pop-zeile--${a.antwort}`}>
                <Icon size={13} aria-hidden="true" />
                <span className="pl-anfragen-pop-name">{a.name}</span>
                <span className="pl-anfragen-pop-antwort">{ANTWORT_INFO[a.antwort].label}</span>
              </span>
            );
          })}
        </span>
        {frist && <span className="pl-anfragen-pop-frist">{frist}</span>}
      </span>
    </span>
  );
}
